import React from 'react';
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies
} from "ebird-mydata-reader";
import {AppScreen} from "./app-screen";
import Welcome from "./views/welcome";
import UploadData from "./views/upload-data";
import SetHomeLocation from "./views/set-home-location";
import Dashboard from "./views/dashboard";
import MapExplorer from "./views/map";
import WrapUp from "./views/wrap-up";
import BigYearExplorer from "./views/discover/big-year";
import FamilyExplorer from "./views/family-explorer";
import HotspotExplorer from "./views/hotspot-explorer";
import CalibrateData from "./views/calibrate-data";
import Discover from "./views/discover";
import LoadingData from "./views/loading-data";
import type {HomeLocation, SpeciesFamilyLink} from "./App";
import type {EnrichedObservationsByLocation} from "./helpers/types";
import type {DashboardSummary} from "./helpers/birdDataCache";
import type {SpeciesMetadataEntry} from "./helpers/speciesMetadataLookup";

export type AppRouterProps = {
    screen: AppScreen,
    rawData: EBirdMyDataSchema[],
    isBirdDataHydrating: boolean,
    isChecklistHydrating: boolean,
    isSpeciesImagesHydrating: boolean,
    lifeList: EBirdMyDataSchema[],
    years: number[]|string[],
    months: number[]|string[],
    year?: number|string,
    month?: number|string,
    mapMode?: string,
    mapTour?: boolean,
    mapActiveSpecies?: number,
    filteredObservations: EBirdMyDataSchema[],
    displayObservationsBySpecies: EBirdObservationsBySpecies[],
    observationsBySpecies: EBirdObservationsBySpecies[],
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>,
    homeLocation?: HomeLocation,
    checklist: EBirdChecklistByFamily[],
    dashboardSummary?: DashboardSummary,
    storedDataRefreshedAt?: string,
    storedCalibrationAppliedAt?: string,
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>,
    speciesImages: any,
    getSpeciesImages: (markers: any) => void,
    lookupByTaxonomicOrder: (taxonomicOrder: number) => SpeciesMetadataEntry|undefined,
    onRequestData: () => void,
    onShowUploadData: () => void,
    onDataLoaded: (data: any[], refreshedAt?: string) => void,
    onCalibrationApplied: (rawCalibrationBundle: string, calibratedAt: string) => Promise<void>,
    onCalibrationCleared: () => Promise<void>,
    onSetYears: React.Dispatch<any>,
    onResetDataRequest: () => void,
    onSetMonth: React.Dispatch<any>,
    onSetYear: React.Dispatch<any>,
    onSetTimePeriod: (year: string, month: string) => void,
    onSetSpeciesFilter: (taxonomicOrder: number) => EBirdObservationsBySpecies|undefined,
    onSetHomeLocation: (location: HomeLocation) => void,
    onSkipHomeLocation: () => void,
    onResetStoredData: () => void,
    onStartBigYear: (year?: string) => void,
    onShowWrapUp: () => void,
    onShowTimePeriodSelector: () => void,
    onShowHomeLocation: () => void,
    onShowFamilyExplorer: () => void,
    onShowHotspotExplorer: () => void
}

export default function AppRouter({
    screen,
    rawData,
    isBirdDataHydrating,
    isChecklistHydrating,
    isSpeciesImagesHydrating,
    lifeList,
    years,
    months,
    year,
    month,
    mapMode,
    mapTour,
    mapActiveSpecies,
    filteredObservations,
    displayObservationsBySpecies,
    observationsBySpecies,
    observationsByLocation,
    homeLocation,
    checklist,
    dashboardSummary,
    storedDataRefreshedAt,
    storedCalibrationAppliedAt,
    familyByTaxonomicOrder,
    parentTaxonomicOrderByTaxonomicOrder,
    speciesImages,
    getSpeciesImages,
    lookupByTaxonomicOrder,
    onRequestData,
    onShowUploadData,
    onDataLoaded,
    onCalibrationApplied,
    onCalibrationCleared,
    onSetYears,
    onResetDataRequest,
    onSetMonth,
    onSetYear,
    onSetTimePeriod,
    onSetHomeLocation,
    onSkipHomeLocation,
    onResetStoredData,
    onStartBigYear,
    onShowWrapUp,
    onShowTimePeriodSelector,
    onShowHomeLocation,
    onShowFamilyExplorer,
    onShowHotspotExplorer
}: AppRouterProps) {
    const renderChecklistLoading = () => (
        <LoadingData
            title="Loading reference data..."
            message="Preparing species and family reference data for this view."
        />
    );
    const renderSpeciesImageLoading = () => (
        <LoadingData
            title="Loading species images..."
            message="Preparing species image references for this view."
        />
    );

    switch (screen) {
        case AppScreen.Loading:
            return <LoadingData />;

        case AppScreen.Welcome:
            return <Welcome onRequestData={onRequestData} onShowUploadData={onShowUploadData} />;

        case AppScreen.Upload:
            return (
                <UploadData
                    onSetData={onDataLoaded}
                    onSetYears={onSetYears}
                    onResetDataRequest={onResetDataRequest}
                />
            );

        case AppScreen.HomeLocation:
            return (
                <SetHomeLocation
                    observationsByLocation={observationsByLocation}
                    selectedHomeLocation={homeLocation}
                    onSetHomeLocation={onSetHomeLocation}
                    onSkipHomeLocation={onSkipHomeLocation}
                />
            );

        case AppScreen.Dashboard:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            if (isSpeciesImagesHydrating) {
                return renderSpeciesImageLoading();
            }
            return (
                <Dashboard
                    homeLocation={homeLocation}
                    dashboardSummary={dashboardSummary}
                    storedDataRefreshedAt={storedDataRefreshedAt}
                    storedCalibrationAppliedAt={storedCalibrationAppliedAt}
                    lifeListLength={lifeList.length}
                    speciesImages={speciesImages}
                    years={years}
                    months={months}
                    onSetMonth={onSetMonth}
                    onSetYear={onSetYear}
                    onSetTimePeriod={onSetTimePeriod}
                    onResetStoredData={onResetStoredData}
                    onShowWrapUp={onShowWrapUp}
                    onShowHomeLocation={onShowHomeLocation}
                    onShowFamilyExplorer={onShowFamilyExplorer}
                />
            );

        case AppScreen.CalibrateData:
            return <CalibrateData years={years} onCalibrationApplied={onCalibrationApplied} onCalibrationCleared={onCalibrationCleared} />;

        case AppScreen.Map:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            if (isSpeciesImagesHydrating) {
                return renderSpeciesImageLoading();
            }
            return (
                <MapExplorer
                    observations={filteredObservations}
                    checklist={checklist}
                    year={year}
                    month={month}
                    homeLocation={homeLocation}
                    observationsByLocation={observationsByLocation}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                    onStartBigYear={onStartBigYear}
                    onShowTimePeriodSelector={onShowTimePeriodSelector}
                    mapMode={mapMode}
                    mapTour={mapTour}
                    activeSpecies={mapActiveSpecies}
                    speciesImages={speciesImages}
                    getSpeciesImages={getSpeciesImages}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.WrapUp:
            return (
                <WrapUp
                    observations={filteredObservations}
                    year={year}
                    month={month}
                />
            );

        case AppScreen.Discover:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            if (isSpeciesImagesHydrating) {
                return renderSpeciesImageLoading();
            }
            return (
                <Discover
                    rawData={rawData}
                    lifeList={lifeList}
                    observationsByLocation={observationsByLocation}
                    checklist={checklist}
                    years={years}
                    homeLocation={homeLocation}
                    speciesImages={speciesImages}
                    onStartBigYear={onStartBigYear}
                />
            );

        case AppScreen.BigYear:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            if (isSpeciesImagesHydrating) {
                return renderSpeciesImageLoading();
            }
            return (
                <BigYearExplorer
                    observations={filteredObservations}
                    speciesImages={speciesImages}
                    year={year}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.FamilyExplorer:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            return <FamilyExplorer checklist={checklist} observationsBySpecies={displayObservationsBySpecies} />;

        case AppScreen.HotspotExplorer:
            if (isChecklistHydrating) {
                return renderChecklistLoading();
            }
            if (isSpeciesImagesHydrating) {
                return renderSpeciesImageLoading();
            }
            return (
                <HotspotExplorer
                    observationsByLocation={observationsByLocation}
                    isBirdDataHydrating={isBirdDataHydrating}
                    lifeListLength={lifeList.length}
                    checklist={checklist}
                    speciesImages={speciesImages}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                    years={years}
                    homeLocation={homeLocation}
                />
            );
    }
}
